import React from 'react';
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { CircleAlert, Download, Eye, Wallet } from 'lucide-react';
import type {
  PendingSummary,
  ProviderPaymentSubmission,
  SubmissionStatus,
} from './types';

interface PaymentsTableProps {
  submissions: ProviderPaymentSubmission[];
  pendingSummary: PendingSummary | null;
  isLoading: boolean;
  isSubmitting: boolean;
  onCreatePayment: () => void;
  onViewSubmission: (submission: ProviderPaymentSubmission) => void;
  onViewOutstanding: () => void;
  onDownloadSubmission: (submission: ProviderPaymentSubmission) => void;
  onDownloadOutstanding: () => void;
}

const statusTag = (
  status: SubmissionStatus
): { variant: React.ComponentProps<typeof Tag>['variant']; label: string } => {
  switch (status) {
    case 'SUBMITTED':
      return { variant: 'success', label: 'Submitted' };
    case 'DUPLICATE':
      return { variant: 'outline-navy', label: 'Duplicate' };
    case 'FAILED':
      return { variant: 'failed', label: 'Failed' };
    case 'IN_PROGRESS':
    default:
      return { variant: 'pending', label: 'In progress' };
  }
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const formatCurrency = (amount: string): string => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `R${n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PaymentsTable = ({
  submissions,
  pendingSummary,
  isLoading,
  isSubmitting,
  onCreatePayment,
  onViewSubmission,
  onViewOutstanding,
  onDownloadSubmission,
  onDownloadOutstanding,
}: PaymentsTableProps) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const pendingCount = pendingSummary?.transactionCount ?? 0;
  const hasOutstanding = pendingCount > 0;
  const unresolvedCount = pendingSummary?.unresolvedCount ?? 0;
  const unresolvedAmount = pendingSummary?.unresolvedAmount ?? '0.00';

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Reference</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead className="text-right">Transactions</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Requested by</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Pinned, synthetic "Outstanding (ready to pay)" row — the computed
              pending_summary aggregate, not a stored submission. */}
          <TableRow className="bg-[rgba(237,244,251,0.6)]">
            <TableCell className="font-semibold">
              <div className="flex items-center gap-1.5">
                <Wallet className="size-4 text-greyscale-text-caption" />
                Outstanding (ready to pay)
              </div>
            </TableCell>
            <TableCell>{pendingSummary?.provider ?? 'FRANC'}</TableCell>
            <TableCell className="text-right font-semibold">
              {pendingCount}
            </TableCell>
            <TableCell className="text-right font-semibold">
              {formatCurrency(pendingSummary?.totalAmount ?? '0.00')}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5">
                <Tag variant={hasOutstanding ? 'pending' : 'outline-navy'}>
                  {hasOutstanding ? 'Ready to pay' : 'Nothing due'}
                </Tag>
                {unresolvedCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help items-center gap-1 text-xs text-greyscale-text-caption">
                        <CircleAlert className="size-4" />
                        {unresolvedCount} unresolved
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {unresolvedCount} transaction
                      {unresolvedCount === 1 ? '' : 's'} (
                      {formatCurrency(unresolvedAmount)}) have no Franc account
                      and are excluded from the payable total until resolved.
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">—</TableCell>
            <TableCell className="text-muted-foreground">
              {pendingSummary ? formatDate(pendingSummary.asOf) : '—'}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={onViewOutstanding}
                      disabled={!hasOutstanding}
                      aria-label="View outstanding transactions">
                      <Eye className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View transactions</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={onDownloadOutstanding}
                      disabled={!hasOutstanding}
                      aria-label="Download outstanding pool CSV">
                      <Download className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download pool CSV</TooltipContent>
                </Tooltip>
                <Button
                  variant="default"
                  size="sm"
                  loading={isSubmitting}
                  disabled={!hasOutstanding}
                  onClick={onCreatePayment}>
                  Create payment
                </Button>
              </div>
            </TableCell>
          </TableRow>

          {/* Immutable submission history. */}
          {submissions.map((submission) => {
            const tag = statusTag(submission.status);
            return (
              <TableRow key={submission.id}>
                <TableCell className="font-mono text-xs font-medium">
                  {submission.transactionReference}
                </TableCell>
                <TableCell>{submission.provider}</TableCell>
                <TableCell className="text-right">
                  {submission.transactionCount}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(submission.totalAmount)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Tag variant={tag.variant}>{tag.label}</Tag>
                    {submission.status === 'FAILED' &&
                      submission.errorDetail && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <CircleAlert className="size-4 cursor-help text-[--greyscale-text-caption]" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {submission.errorDetail}
                          </TooltipContent>
                        </Tooltip>
                      )}
                  </div>
                </TableCell>
                <TableCell>{submission.requestedByName ?? '—'}</TableCell>
                <TableCell>{formatDate(submission.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onViewSubmission(submission)}
                          aria-label="View payment transactions">
                          <Eye className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View transactions</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onDownloadSubmission(submission)}
                          aria-label="Download payment CSV">
                          <Download className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Download CSV</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};

export default PaymentsTable;
